/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { Panel } from "../../../../ui/components/Panel";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { BEM, TTranslateHookFn, useTranslate } from "@adapptio/lib-components";
import { useTreeState } from "../../../../ui/components/Tree/useTreeState";
import { TreeView } from "../../../../ui/components/Tree/TreeView";
import { ITreeNode, TREE_VIEW_STYLE } from "../../../../ui/components/Tree/TreeViewTypes";
import { BlueprintActionNode_v2, NODE_TYPES, TBlueprintEndpointSchema } from "@adapptio/lib-core";
import {
	ISchemaFlowNodeTypeDefinition,
	isSchemaFlowNodeListModel,
	isSchemaFlowNodeModel,
	TGenericBlueprintSchema,
	TGetBlueprintSchemaModel
} from "@adapptio/lib-blueprint";
import { ToolbarTitle } from "../../../../ui/components/Toolbar/ToolbarTitle";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { EndpointEditorEditView } from "../EndpointEditorEditView";

function getNodeTree(
	t: TTranslateHookFn,
	model: TGetBlueprintSchemaModel<TBlueprintEndpointSchema>,
	view: EndpointEditorEditView
): ITreeNode[] {
	const nodeList = model.props.spec.constant.props.nodes.value;

	const items = nodeList.items.map((item) => {
		const itemType = item.schema.opts.nodeTypes[
			item.type
		] as ISchemaFlowNodeTypeDefinition<TGenericBlueprintSchema>;

		let typeLabel: string;
		let elementLabel: string;
		let icon: string;

		const varName = item.varName.value && item.varName.value != "" ? item.varName.value : undefined;
		const comment = item.comment.value && item.comment.value != "" ? item.comment.value.split("\n")[0] : null;

		if (item.type === NODE_TYPES.ACTION_V2) {
			const actionOpts = item.opts.template as TGetBlueprintSchemaModel<typeof BlueprintActionNode_v2.opts>;
			const resourceDesc = actionOpts.constant && actionOpts.constant.props.action.actionId.constant
				? view.getRelatedResourceDescrption(actionOpts.constant.props.action.actionId.constant.value)
				: undefined;

			icon = itemType.icon;
			typeLabel = t("editor", itemType.label);
			elementLabel = resourceDesc?.name;
		} else {
			icon = itemType.icon;
			typeLabel = t("editor", itemType.label);
		}

		const label = varName ?? comment ?? elementLabel ?? typeLabel;
		const description = label !== typeLabel ? typeLabel : undefined;

		return {
			id: String(item.nodeId),
			label: label,
			description: description,
			icon: icon,
			children: null,
			isLoadable: false,
			selectable: true,
			metaData: {
				nodeList: nodeList,
				modelNode: item
			}
		};
	});

	const rootClass = BEM("edt-module-endpoint-editor-outline__tree-node-section", null, {
		"is-empty": items.length === 0
	});

	return [
		{
			id: "root",
			label: "Nodes",
			icon: "mdi/hubspot",
			defaultExpanded: true,
			children: items.length > 0 ? items : null,
			className: rootClass.toClassName()
		}
	];
}

/*
 * Toolbar
 */
export interface IEndpointEditorOutlineToolbarProps {
	view: EndpointEditorEditView;
}

export const EndpointEditorOutlineToolbar: FC<IEndpointEditorOutlineToolbarProps> = () => {
	// @todo translate
	return (
		<>
			<ToolbarTitle>Outline</ToolbarTitle>
			<ToolbarSpacer grow={1} />
		</>
	);
};

/*
 * Content
 */
export interface IEndpointEditorOutlineContentProps {
	view: EndpointEditorEditView;
}

export const EndpointEditorOutlineContent: FC<IEndpointEditorOutlineContentProps> = ({ view }) => {
	const t = useTranslate();
	const endpointModel = view.getEndpointModel();

	const baseClass = BEM("edt-module-endpoint-editor-outline");

	const [ modelRef, setModelRef ] = useState(() => ({
		model: endpointModel.getModel()
	}));

	const [ nodeTreeState, handleNodeTreeStateUpdate ] = useTreeState({
		nodes: { root: { expanded: true } }
	});

	const [ selection, setSelection ] = useState([]);

	// Tree items
	const nodeTreeItems = useMemo(() => {
		return getNodeTree(t, modelRef.model, view);
	}, [ modelRef, view ]);

	// Nodes
	const handleNodeSelect = useCallback((node: ITreeNode) => {
		const modelNode = node.metaData?.modelNode;
		const nodeList = node.metaData?.nodeList;

		if (isSchemaFlowNodeModel(modelNode) && isSchemaFlowNodeListModel(nodeList)) {
			endpointModel.getSelection().selectFlowNode(nodeList, modelNode);
		}
	}, []);

	useEffect(() => {
		const handleUpdate = () =>
			setModelRef({
				model: endpointModel.getModel()
			});

		const handleSelectionChange = () =>
			setSelection(
				endpointModel
					.getSelection()
					.getItems()
					.map((item) => String(item.nodeId))
			);

		onEvent(endpointModel.onModelChange, handleUpdate);
		onEvent(endpointModel.onRender, handleUpdate);
		onEvent(endpointModel.getSelection().onChange, handleSelectionChange);

		return () => {
			offEvent(endpointModel.onModelChange, handleUpdate);
			offEvent(endpointModel.onRender, handleUpdate);
			offEvent(endpointModel.getSelection().onChange, handleSelectionChange);
		};
	}, [ endpointModel ]);

	return (
		<Panel scroll={true} className={baseClass.toClassName()}>
			<div className={baseClass.element("nodes").toClassName()}>
				<TreeView
					items={nodeTreeItems}
					state={nodeTreeState}
					selections={selection}
					onNodeSelect={handleNodeSelect}
					onStateUpdate={handleNodeTreeStateUpdate}
					style={TREE_VIEW_STYLE.DEFAULT}
					compact={false}
				/>
				{!nodeTreeItems[0].children ? (
					<div className={baseClass.element("note").toClassName()}>
						Add nodes by dragging them from the right panel.
					</div>
				) : null}
			</div>
		</Panel>
	);
};
